// Phase compiler-help-modal — discoverability surface for Compiler
// features that are present but invisible to a new publisher: undo,
// drag-to-reclassify, sibling sequences, lock/unlock, publish wizard.
//
// Triggered by the "?" button on CompilerToolbar (or Shift+? globally
// when the modal is closed). Closes on Escape, on backdrop click, or
// on the Close button. Two-column layout on wide viewports; single
// column on narrow.

import { MovableDialog } from '@/components/shared/MovableDialog';
import { useEffect, useRef, type CSSProperties, type ReactElement } from 'react';

export interface CompilerHelpModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
}




const bodyStyle: CSSProperties = {
  padding: '16px 20px',
  // Two columns on wide screens, single column on narrow.
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: '16px 24px',
  fontSize: 13,
  lineHeight: 1.5,
};

const sectionTitleStyle: CSSProperties = {
  fontWeight: 600,
  marginBottom: 4,
  color: 'var(--text)',
  fontSize: 13,
};

const kbdStyle: CSSProperties = {
  display: 'inline-block',
  padding: '1px 6px',
  fontSize: 11,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  border: '1px solid var(--border)',
  borderRadius: 4,
  background: 'var(--surface2, #f5f5f5)',
  color: 'var(--text)',
  minWidth: 18,
  textAlign: 'center',
};

const footerStyle: CSSProperties = {
  padding: '10px 20px',
  borderTop: '1px solid var(--border)',
  display: 'flex',
  justifyContent: 'flex-end',
};

const closeBtnStyle: CSSProperties = {
  background: 'var(--accent, #4f46e5)',
  color: '#fff',
  border: '1px solid var(--accent, #4f46e5)',
  borderRadius: 4,
  padding: '6px 14px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};

export function CompilerHelpModal({ open, onClose }: CompilerHelpModalProps): ReactElement | null {
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  // Close on Escape; focus the Close button when opened.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    closeBtnRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [open, onClose]);

  if (!open) return null;

  // phase-movable-dialogs — shared movable shell (its own Escape handler above
  // is kept: it also focuses Close on open, which the shell does not do).
  return (
    <MovableDialog
      persistKey="compiler.help"
      title={
        <span id="compiler-help-modal-title">
          Compiler Keyboard Shortcuts &amp; Features
        </span>
      }
      ariaLabelledBy="compiler-help-modal-title"
      testId="compiler-help-modal"
      overlayTestId="compiler-help-modal-backdrop"
      onClose={onClose}
      closeOnBackdrop
      width={640}
      maxHeight="82vh"
    >

        <div style={bodyStyle}>
          <section data-testid="compiler-help-section-shortcuts">
            <div style={sectionTitleStyle}>Keyboard shortcuts</div>
            <div>
              <kbd style={kbdStyle}>Ctrl</kbd> + <kbd style={kbdStyle}>Z</kbd> — Undo last
              action (up to 20 steps)
            </div>
            <div>
              <kbd style={kbdStyle}>Ctrl</kbd> + <kbd style={kbdStyle}>⇧</kbd> +{' '}
              <kbd style={kbdStyle}>Z</kbd> — Redo (when available)
            </div>
            <div>
              <kbd style={kbdStyle}>⇧</kbd> + <kbd style={kbdStyle}>?</kbd> — Open this
              help modal
            </div>
            <div>
              <kbd style={kbdStyle}>Esc</kbd> — Close this modal
            </div>
          </section>

          <section data-testid="compiler-help-section-drag">
            <div style={sectionTitleStyle}>Drag &amp; drop</div>
            <div>
              Drag a document row to a different section to reclassify it. A
              confirmation dialog appears before the move is applied.
            </div>
          </section>

          <section data-testid="compiler-help-section-siblings">
            <div style={sectionTitleStyle}>Sibling sequences</div>
            <div>
              Use the sequence selector dropdown to view documents from prior
              sequences of the same application.
            </div>
          </section>

          <section data-testid="compiler-help-section-lock">
            <div style={sectionTitleStyle}>Lock / Unlock</div>
            <div>
              Lock a document to prevent accidental changes. Locked documents cannot
              be moved or reclassified.
            </div>
          </section>

          <section data-testid="compiler-help-section-publish">
            <div style={sectionTitleStyle}>Publish wizard</div>
            <div>
              When all documents are confirmed and validation passes, the Publish
              button opens a 4-step wizard to compile, sign, and transmit to the
              agency.
            </div>
          </section>
        </div>

        <div style={footerStyle}>
          <button
            ref={closeBtnRef}
            type="button"
            data-testid="compiler-help-modal-close-btn"
            onClick={onClose}
            style={closeBtnStyle}
          >
            Close
          </button>
        </div>
    </MovableDialog>
  );
}
